import FlexFormContainer from "../common/FlexFormContainer";
import { Controller, useForm } from "react-hook-form";
import {
  Button,
  Flex,
  SelectField,
  TextAreaField,
  TextField,
  Text,
  Label,
} from "@aws-amplify/ui-react";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../common/useDateFunctions";
import DatePicker from "rsuite/DatePicker";
import { zodResolver } from "@hookform/resolvers/zod";
import { useUserQuery } from "../common/useUserQuery";
import { useVitalsPostMutation } from "./api/useVitalsMutation";
import { useVitalsCatalogQuery } from "./api/useVitalsQuery";
import { useEffect, useState } from "react";
const { z } = require("zod");

const postRequest = z.object({
  type: z.string().min(1, "Required").max(30),
  date: z.date({ message: "Required" }),
  value: z.string().min(1, "Required").max(15),
  unit: z.string().min(1, "Required").max(30),
  notes: z.string().max(250).nullish(),
});

export default function VitalsAddPage({
  vitalUUID = undefined,
  onPostAdd = undefined,
}) {
  const navigate = useNavigate();
  const [returnData, setReturnData] = useState(null);
  const { data: userQueryData } = useUserQuery();
  const { data: vitalsCatalog, isLoading: isLoadingVitalsCatalog } =
    useVitalsCatalogQuery();
  const {
    mutate: vitalsPostMutation,
    isLoading: isLoadingVitalsPost,
    isSuccess: isSuccessVitalsPost,
    error: errorVitalsPost,
  } = useVitalsPostMutation();
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(postRequest),
  });

  useEffect(() => {
    if (isSuccessVitalsPost) {
      if (onPostAdd) {
        onPostAdd(returnData);
      }

      if (!vitalUUID) {
        navigate("/user/hr/vitals");
      }
    }
  }, [isSuccessVitalsPost]);

  const onSubmit = (values) => {
    const requestPayload = {
      ...values,
      id: vitalUUID,
      patientUserId: userQueryData?.patientUserId,
      date: formatDate(new Date(values.date)),
    };
    setReturnData(requestPayload);
    vitalsPostMutation(requestPayload);
    reset();
  };

  if (isLoadingVitalsPost) return "Saving...";

  if (isLoadingVitalsCatalog) return "Loading...";

  if (errorVitalsPost)
    return "There was an error processing your request. Try again later.";

  return (
    <>
      <form id="vitals-add-form" onSubmit={handleSubmit(onSubmit)}>
        <FlexFormContainer headerTitle={"Vitals Details"}>
          <Flex
            direction="column"
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <SelectField
                  label="Type"
                  placeholder="Select"
                  {...field}
                  onChange={(e) => {
                    const vitalCatalogItem = vitalsCatalog?.find(
                      (vc) => vc?.name === e.target.value
                    );
                    if (vitalCatalogItem) {
                      const parsedUnits = JSON.parse(vitalCatalogItem?.units);
                      setValue("unit", parsedUnits[0]);
                    }
                    field.onChange(e);
                  }}
                  errorMessage={errors.type?.message}
                  hasError={!!errors.type}
                >
                  {vitalsCatalog?.map((vc) => (
                    <option key={`vc_${vc?.id}`} value={vc?.name}>
                      {vc?.name}
                    </option>
                  ))}
                </SelectField>
              )}
            />

            <Label htmlFor="date">Date</Label>

            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <Flex direction={"column"}>
                  <DatePicker
                    onChange={(value) => field.onChange(value)}
                    value={field.value || null}
                    format="yyyy-MM-dd"
                    oneTap
                  />
                  {!!errors?.date && (
                    <Text
                      variation="error"
                      as="p"
                      lineHeight="0.5em"
                      fontWeight={400}
                      fontSize="0.9em"
                      fontStyle="normal"
                      textDecoration="none"
                    >
                      {errors?.date.message}
                    </Text>
                  )}
                </Flex>
              )}
            />

            <Controller
              name="value"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Value"
                  {...field}
                  value={field.value || ""}
                  errorMessage={errors.value?.message}
                  hasError={!!errors.value}
                />
              )}
            />

            <Controller
              name="unit"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Unit"
                  disabled={true}
                  value={field.value || ""}
                  errorMessage={errors.unit?.message}
                  hasError={!!errors.unit}
                />
              )}
            />

            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextAreaField
                  label="Notes"
                  {...field}
                  value={field.value || ""}
                  errorMessage={errors.notes?.message}
                  hasError={!!errors.notes}
                />
              )}
            />

            <Button variation="primary" onClick={handleSubmit(onSubmit)}>
              Save
            </Button>
          </Flex>
        </FlexFormContainer>
      </form>
    </>
  );
}
